import { useEffect, useRef, useState } from "react";
import { useParams, useNavigate } from "react-router-dom";
import SiteHeader from "@/components/SiteHeader";
import SiteFooter from "@/components/SiteFooter";
import { api, formatApiError } from "@/lib/api";
import { toast } from "sonner";
import { CheckCircle2, ArrowRight, Sparkles } from "lucide-react";

const STORAGE_KEY = (id) => `intake-${id}`;

export default function IntakeChat() {
  const { sessionId } = useParams();
  const navigate = useNavigate();
  const [step, setStep] = useState(null);
  const [progress, setProgress] = useState({ current: 1, total: 1 });
  const [opening, setOpening] = useState("");
  const [history, setHistory] = useState([]); // [{question, answerLabel}]
  const [textValue, setTextValue] = useState("");
  const [loading, setLoading] = useState(false);
  const [complete, setComplete] = useState(false);
  const [matterId, setMatterId] = useState(null);
  const [portalToken, setPortalToken] = useState(null);
  const [summary, setSummary] = useState(null);
  const inputRef = useRef(null);

  // hydrate the first step from sessionStorage (stashed in IntakeStart.jsx)
  useEffect(() => {
    const stored = sessionStorage.getItem(STORAGE_KEY(sessionId));
    if (stored) {
      const parsed = JSON.parse(stored);
      if (parsed.opening) setOpening(parsed.opening);
      if (parsed.step) setStep(parsed.step);
      if (parsed.progress) setProgress(parsed.progress);
    } else {
      // session id only — nothing to resume from, show fallback
      setStep({
        id: "fallback",
        prompt: "Let's continue your intake. What is your full name?",
        type: "text",
        section: "Your details",
        required: true,
      });
    }
  }, [sessionId]);

  // autofocus text input on each step change
  useEffect(() => {
    if (step && ["text", "longtext", "email", "phone", "number", "currency", "date"].includes(step.type)) {
      setTimeout(() => inputRef.current?.focus(), 50);
    }
    setTextValue("");
  }, [step?.id]);

  const submitAnswer = async (rawValue, displayLabel) => {
    if (loading || complete || !step) return;
    const value = (rawValue ?? "").toString().trim();
    if (!value && step.required) {
      toast.error("Please provide an answer.");
      return;
    }
    setLoading(true);
    try {
      const { data } = await api.post("/intake/message", {
        session_id: sessionId,
        message: value,
      });

      setHistory((h) => [
        ...h,
        {
          question: step.prompt,
          answer:   displayLabel || value,
          section:  step.section,
        },
      ]);

      if (data.complete) {
        setComplete(true);
        setMatterId(data.matter_id);
        setPortalToken(data.portal_token || null);
        setSummary(data.summary || null);
        setStep(null);
        setProgress(data.progress || progress);
        toast.success("Thanks — your matter has been created.");
        sessionStorage.removeItem(STORAGE_KEY(sessionId));
        return;
      }
      // Some answer-validation errors return the same step with an error.
      if (data.error) {
        toast.error(data.error);
      }
      setStep(data.step || null);
      setProgress(data.progress || progress);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  const handleTextSubmit = (e) => {
    e?.preventDefault?.();
    submitAnswer(textValue);
  };

  return (
    <div className="min-h-screen bg-background grain flex flex-col">
      <SiteHeader />

      <section className="border-b border-zinc-200 bg-zinc-50">
        <div className="mx-auto max-w-3xl px-6 py-8">
          <div className="flex items-center gap-2 text-overline text-zinc-500">
            <Sparkles className="h-3 w-3" /> Guided intake
          </div>
          <h1 className="mt-3 font-serif text-3xl lg:text-4xl font-medium tracking-tight">
            Tell us about your matter
          </h1>
          <p className="mt-2 text-sm text-zinc-600">
            One question at a time. An admitted attorney will review everything before anything legally binding happens.
          </p>

          {!complete && progress.total > 0 && (
            <div className="mt-6" data-testid="intake-progress">
              <div className="flex items-center justify-between text-[11px] tracking-[0.18em] uppercase text-zinc-500">
                <span>{step?.section || "Getting started"}</span>
                <span data-testid="intake-progress-label">
                  Step {progress.current} of {progress.total}
                </span>
              </div>
              <div className="mt-2 h-1 w-full bg-zinc-200">
                <div
                  className="h-1 bg-zinc-900 transition-all duration-300"
                  style={{
                    width: `${Math.min(
                      100,
                      Math.round(((progress.current - 1) / Math.max(progress.total, 1)) * 100),
                    )}%`,
                  }}
                />
              </div>
            </div>
          )}
        </div>
      </section>

      <section className="flex-1 mx-auto max-w-3xl w-full px-6 py-10">
        {/* Already-answered list — short context, no re-asks. */}
        {history.length > 0 && (
          <ol className="mb-6 space-y-2" data-testid="intake-history">
            {history.map((h, i) => (
              <li
                key={i}
                className="flex items-start gap-3 text-sm text-zinc-600 bg-zinc-50 border border-zinc-100 px-4 py-2"
                data-testid={`intake-history-item-${i}`}
              >
                <CheckCircle2 className="h-4 w-4 text-zinc-400 mt-0.5 shrink-0" />
                <div className="min-w-0">
                  <div className="text-[10px] tracking-[0.18em] uppercase text-zinc-400">{h.section}</div>
                  <div className="text-zinc-500 truncate">{h.question}</div>
                  <div className="text-zinc-900 font-medium truncate" data-testid={`intake-history-answer-${i}`}>
                    {h.answer}
                  </div>
                </div>
              </li>
            ))}
          </ol>
        )}

        {!complete && step && (
          <div className="border border-zinc-200 bg-white p-6 lg:p-8" data-testid="intake-step">
            {opening && history.length === 0 && (
              <div className="text-sm text-zinc-500 mb-4" data-testid="intake-opening">{opening}</div>
            )}
            <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500">{step.section}</div>
            <h2 className="mt-2 font-serif text-2xl lg:text-3xl tracking-tight" data-testid="intake-question">
              {step.prompt}
            </h2>

            {/* Choice / yes-no — render as buttons */}
            {(step.type === "choice" || step.type === "yesno") && step.options && (
              <div className="mt-6 flex flex-wrap gap-3" data-testid="intake-choices">
                {step.options.map((opt) => (
                  <button
                    key={opt.value}
                    onClick={() => submitAnswer(opt.value, opt.label)}
                    disabled={loading}
                    className="border border-zinc-200 px-5 py-3 text-sm hover:border-zinc-900 hover:bg-zinc-900 hover:text-white transition-colors disabled:opacity-50"
                    data-testid={`intake-choice-${opt.value}`}
                  >
                    {opt.label}
                  </button>
                ))}
              </div>
            )}

            {/* Text / email / phone / number / currency / date — input field */}
            {!["choice", "yesno"].includes(step.type) && (
              <form onSubmit={handleTextSubmit} className="mt-6 flex flex-col sm:flex-row gap-3">
                {step.type === "longtext" ? (
                  <textarea
                    ref={inputRef}
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    rows={3}
                    placeholder="Type your answer…"
                    className="flex-1 px-4 py-3 border border-zinc-200 outline-none focus:border-zinc-900 transition-colors text-sm resize-none"
                    data-testid="intake-input"
                  />
                ) : (
                  <input
                    ref={inputRef}
                    type={
                      step.type === "email" ? "email" :
                      step.type === "phone" ? "tel" :
                      step.type === "date"  ? "date" :
                      step.type === "number" || step.type === "currency" ? "text" :
                      "text"
                    }
                    inputMode={
                      step.type === "number" || step.type === "currency" ? "decimal" :
                      step.type === "phone" ? "tel" : undefined
                    }
                    value={textValue}
                    onChange={(e) => setTextValue(e.target.value)}
                    placeholder={
                      step.type === "currency" ? "e.g. 2500000" :
                      step.type === "email"    ? "name@example.com" :
                      step.type === "phone"    ? "082 000 0000" :
                      step.type === "date"     ? "" :
                      "Type your answer…"
                    }
                    className="flex-1 px-4 py-3 border border-zinc-200 outline-none focus:border-zinc-900 transition-colors text-sm"
                    data-testid="intake-input"
                  />
                )}
                <button
                  type="submit"
                  disabled={loading || (!textValue.trim() && step.required)}
                  className="bg-zinc-900 text-white px-6 py-3 text-sm hover:bg-zinc-800 transition-colors disabled:opacity-50 inline-flex items-center gap-2 justify-center"
                  data-testid="intake-submit"
                >
                  {loading ? "…" : "Continue"} <ArrowRight className="h-4 w-4" />
                </button>
              </form>
            )}

            {!step.required && (
              <button
                onClick={() => submitAnswer("", "(skipped)")}
                disabled={loading}
                className="mt-4 text-xs text-zinc-500 hover:text-zinc-900 underline"
                data-testid="intake-skip"
              >
                Skip this question
              </button>
            )}
          </div>
        )}

        {complete && (
          <div className="border border-zinc-200 bg-white" data-testid="intake-complete">
            <div className="px-6 py-5 border-b border-zinc-200 flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-green-600 shrink-0" />
              <div>
                <div className="font-semibold" data-testid="intake-complete-headline">
                  You&apos;re all set — we&apos;ve created your matter.
                </div>
                <div className="text-sm text-zinc-600" data-testid="intake-complete-secondary">
                  We&apos;ve also sent you a link so you can return to your matter at any time.
                </div>
              </div>
            </div>

            <div className="divide-y divide-zinc-200" data-testid="intake-summary">
              {/* 1 — Here's what we understand */}
              <SummarySection title="Here's what we understand" testid="summary-understand">
                {summary?.understand?.length ? (
                  <ul className="space-y-2 text-sm text-zinc-700 leading-relaxed">
                    {summary.understand.map((line, i) => (
                      <li key={i} className="flex gap-2" data-testid={`summary-understand-${i}`}>
                        <span className="text-zinc-400">·</span>
                        <span>{line}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-zinc-600">
                    We&apos;ve captured your answers. Our team will confirm the details with you shortly.
                  </p>
                )}
              </SummarySection>

              {/* 2 — What we still need */}
              <SummarySection title="What we still need" testid="summary-need">
                {summary?.missing?.length ? (
                  <ul className="space-y-2 text-sm text-zinc-700 leading-relaxed">
                    {summary.missing.map((item, i) => (
                      <li key={i} className="flex gap-2" data-testid={`summary-missing-${i}`}>
                        <span className="text-amber-600">!</span>
                        <span>{item}</span>
                      </li>
                    ))}
                  </ul>
                ) : (
                  <p className="text-sm text-zinc-700 flex items-center gap-2">
                    <CheckCircle2 className="h-4 w-4 text-green-600" />
                    Everything needed has been captured. You can proceed.
                  </p>
                )}
              </SummarySection>

              {/* 3 — Important to note */}
              {summary?.important?.length > 0 && (
                <SummarySection title="Important to note" testid="summary-important">
                  <ul className="space-y-3">
                    {summary.important.map((r, i) => (
                      <li
                        key={i}
                        className="text-sm text-zinc-700 leading-relaxed"
                        data-testid={`summary-risk-${i}`}
                      >
                        <div className="flex items-center gap-2">
                          <span
                            className={`text-[10px] tracking-[0.18em] uppercase px-1.5 py-0.5 ${
                              r.severity === "high"
                                ? "bg-red-100 text-red-800"
                                : r.severity === "medium"
                                ? "bg-amber-100 text-amber-800"
                                : "bg-zinc-100 text-zinc-700"
                            }`}
                          >
                            {r.severity}
                          </span>
                          <span className="font-medium text-zinc-900">{r.title}</span>
                        </div>
                        {r.detail && (
                          <div className="mt-1 text-zinc-600">{r.detail}</div>
                        )}
                      </li>
                    ))}
                  </ul>
                </SummarySection>
              )}

              {/* 4 — Next steps */}
              <SummarySection title="Next steps" testid="summary-next">
                {summary?.next_steps?.length ? (
                  <ol className="space-y-3">
                    {summary.next_steps.map((s, i) => (
                      <li
                        key={i}
                        className="flex gap-3 text-sm text-zinc-700 leading-relaxed"
                        data-testid={`summary-next-${i}`}
                      >
                        <span className="font-mono text-xs text-zinc-400 pt-0.5 shrink-0">
                          {String(i + 1).padStart(2, "0")}
                        </span>
                        <div className="min-w-0">
                          <div className="font-medium text-zinc-900">{s.label}</div>
                          <div className="mt-0.5 flex flex-wrap items-center gap-x-3 gap-y-1 text-[11px]">
                            <span className="text-zinc-500 uppercase tracking-[0.14em]">
                              {s.owner_label}
                            </span>
                            {s.blocking && (
                              <span className="text-amber-700 uppercase tracking-[0.14em]">
                                Blocking
                              </span>
                            )}
                            {s.deadline && (
                              <span className="text-zinc-500">by {s.deadline}</span>
                            )}
                          </div>
                          {s.rationale && (
                            <div className="mt-1 text-zinc-500">{s.rationale}</div>
                          )}
                        </div>
                      </li>
                    ))}
                  </ol>
                ) : (
                  <p className="text-sm text-zinc-700">
                    An admitted attorney will review your matter within one business day. You&apos;ll
                    receive an email with confirmation and your private client portal link.
                  </p>
                )}
              </SummarySection>
            </div>

            <div className="px-6 py-5 border-t border-zinc-200 flex flex-wrap gap-3 items-center">
              {portalToken ? (
                <button
                  onClick={() => navigate(`/client/${portalToken}`)}
                  className="inline-flex items-center gap-2 bg-zinc-900 text-white px-6 py-3 text-sm font-medium hover:bg-zinc-800 transition-colors"
                  data-testid="intake-go-to-matter"
                >
                  Go to your matter <ArrowRight className="h-4 w-4" />
                </button>
              ) : (
                /* P0 fallback — never a dead-end. We surface a soft state +
                   give the user something to do (refresh via summary endpoint)
                   so they don't have to wait on backoffice. */
                <div
                  className="text-sm text-amber-800 bg-amber-50 border border-amber-200 px-4 py-3"
                  data-testid="intake-portal-pending"
                >
                  We&apos;re finalising your matter — please wait a moment and
                  <button
                    onClick={() => window.location.reload()}
                    className="underline ml-1"
                  >refresh</button> to continue.
                </div>
              )}
              {portalToken && summary?.missing?.length > 0 && (
                <button
                  onClick={() => navigate(`/client/${portalToken}`)}
                  className="inline-flex items-center gap-2 border border-zinc-200 text-zinc-900 px-5 py-3 text-sm hover:border-zinc-900 transition-colors"
                  data-testid="intake-upload-documents"
                >
                  Upload documents <ArrowRight className="h-4 w-4" />
                </button>
              )}
              <button
                onClick={() => navigate("/")}
                className="inline-flex items-center gap-2 text-sm text-zinc-600 hover:text-zinc-900 transition-colors"
                data-testid="intake-back-home"
              >
                Back to home
              </button>
              {matterId && (
                <span className="text-xs text-zinc-500 ml-auto" data-testid="intake-matter-ref">
                  Reference: {matterId.slice(0, 8)}…
                </span>
              )}
            </div>
          </div>
        )}

        <p className="mt-6 text-center text-xs text-zinc-500">Information shared here is treated as legally privileged.</p>
      </section>

      <SiteFooter />
    </div>
  );
}

function SummarySection({ title, body, children, testid }) {
  return (
    <div className="px-6 py-5" data-testid={testid}>
      <div className="text-[11px] tracking-[0.18em] uppercase text-zinc-500">{title}</div>
      <div className="mt-3">
        {children !== undefined ? children : (
          <p className="text-sm text-zinc-700 leading-relaxed">{body}</p>
        )}
      </div>
    </div>
  );
}
